<template>
  <div class="adash">
    <section class="adash__kpis" data-tour="admin-dash-kpi">
      <router-link
        v-for="card in kpis"
        :key="card.label"
        :to="card.to"
        class="sui-card adash__kpi"
        :class="{ 'is-alert': card.alert }"
      >
        <span class="adash__kpi-icon" :class="`adash__kpi-icon--${card.tone}`">
          <AppIcon :icon="card.icon" :size="20" />
        </span>
        <span class="adash__kpi-label">{{ card.label }}</span>
        <span class="adash__kpi-value">{{ card.value }}<em>{{ card.unit }}</em></span>
      </router-link>
    </section>

    <section
      v-if="ops.taxInvoicePending > 0"
      class="sui-card sui-card--flush adash__tax"
      data-tour="admin-dash-tax"
    >
      <div class="sui-card__head">
        <h2 class="sui-card__title">
          세금계산서 발행 대기 <em>{{ ops.taxInvoicePending }}건</em>
        </h2>
        <router-link to="/admin/orders?tax=1" class="sui-card__link">
          주문·입금에서 보기 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>

      <div class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>주문</th>
              <th>기관 · 상호</th>
              <th>수신 이메일</th>
              <th class="is-num">금액</th>
              <th>요청</th>
              <th class="is-center">처리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in ops.taxInvoiceQueue" :key="row.code">
              <td>
                <span class="sui-table__strong">{{ row.code }}</span>
                <span class="sui-table__sub">{{ formatDate(row.orderedAt) }}</span>
              </td>
              <td>
                <span>{{ row.orgName || '—' }}</span>
                <span v-if="row.bizName" class="sui-table__sub">{{ row.bizName }}</span>
              </td>
              <td>
                <span class="adash__email">{{ row.taxEmail || '—' }}</span>
              </td>
              <td class="is-num sui-table__strong">{{ formatWon(row.totalAmount) }}원</td>
              <td>
                <span class="sui-table__sub">{{ formatDate(row.taxAt) }}</span>
              </td>
              <td class="is-center">
                <router-link
                  :to="{ path: '/admin/orders', query: { tax: '1', keyword: row.code } }"
                  class="sui-btn sui-btn--sm sui-btn--primary"
                >
                  확인하기
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div class="adash__cols">
      <section class="sui-card sui-card--flush adash__requests" data-tour="admin-dash-queue">
        <div class="sui-card__head">
          <h2 class="sui-card__title">
            검수요청 <em>{{ requested.length }}건</em>
          </h2>
          <router-link to="/admin/products?status=REQUESTED" class="sui-card__link">
            전체 보기 <AppIcon icon="mdi-chevron-right" :size="15" />
          </router-link>
        </div>

        <div v-if="requested.length" class="sui-table-wrap">
          <table class="sui-table">
            <thead>
              <tr>
                <th>상품</th>
                <th>판매업체</th>
                <th class="is-num">판매가</th>
                <th>요청</th>
                <th class="is-center">처리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in requested.slice(0, 8)" :key="product.code">
                <td>
                  <span class="sui-prod">
                    <img
                      v-if="product.thumbnail"
                      class="sui-prod__img"
                      :src="product.thumbnail"
                      :alt="product.name"
                      loading="lazy"
                    />
                    <span v-else class="adash__noimg">
                      <AppIcon icon="mdi-image-off-outline" :size="16" />
                    </span>
                    <span>
                      <span class="sui-prod__name">{{ product.name }}</span>
                      <span class="sui-table__sub">{{ product.code }} · {{ product.category }}</span>
                    </span>
                  </span>
                </td>
                <td>
                  <router-link
                    v-if="product.storeName"
                    class="adash__store-link"
                    :to="{
                      path: '/admin/products',
                      query: { status: 'REQUESTED', keyword: product.storeName },
                    }"
                  >
                    {{ product.storeName }}
                  </router-link>
                  <span v-else>—</span>
                </td>
                <td class="is-num sui-table__strong">{{ formatWon(product.price) }}원</td>
                <td>
                  <span class="sui-table__sub">{{ formatDate(product.requestedAt) }}</span>
                  <span v-if="product.requestNote" class="adash__note">{{ product.requestNote }}</span>
                </td>
                <td class="is-center">
                  <router-link :to="`/admin/products/${product.code}`" class="sui-btn sui-btn--sm sui-btn--primary">
                    검수하기
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="sui-empty">
          <AppIcon icon="mdi-check-circle-outline" :size="30" />
          <p>{{ loading ? '불러오는 중입니다…' : '검수를 기다리는 상품이 없습니다.' }}</p>
        </div>
      </section>

      <aside class="adash__side">
        <section class="sui-card adash__todo" data-tour="admin-dash-links">
          <div class="sui-card__head">
            <h2 class="sui-card__title">
              바로 처리할 일 <em>{{ taskTotal }}건</em>
            </h2>
          </div>
          <div class="sui-card__body">
            <div class="adash__todo-grid">
              <router-link v-for="task in tasks" :key="task.label" :to="task.to" class="adash__task">
                <span class="adash__task-count" :class="{ 'is-zero': !task.count }">{{ task.count }}</span>
                <span class="adash__task-label">{{ task.label }}</span>
                <AppIcon class="adash__task-arrow" icon="mdi-chevron-right" :size="16" />
              </router-link>
            </div>
            <p class="sui-hint adash__todo-note">입금 확인 → 상품관리(검수) → 클레임·환불 순으로 처리하면 막힘이 줄어듭니다.</p>
          </div>
        </section>

        <section class="sui-card adash__guide">
          <div class="sui-card__head">
            <h2 class="sui-card__title">검수 순서</h2>
          </div>
          <div class="sui-card__body">
            <ol class="adash__steps">
              <li v-for="step in steps" :key="step.title">
                <strong>{{ step.title }}</strong>
                <p>{{ step.desc }}</p>
              </li>
            </ol>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchAdminOpsKpis, type AdminOpsKpis } from '@/api/admin'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { formatWon } from '@/constants/sellerData'

const { products, counts, loading, load } = useAdminProducts()

const ops = ref<AdminOpsKpis>({
  pendingPayments: 0,
  payNotified: 0,
  claimPending: 0,
  unshipped: 0,
  taxInvoicePending: 0,
  taxInvoiceQueue: [],
})

onMounted(async () => {
  await load()
  try {
    ops.value = await fetchAdminOpsKpis()
  } catch {
    ops.value = {
      pendingPayments: 0,
      payNotified: 0,
      claimPending: 0,
      unshipped: 0,
      taxInvoicePending: 0,
      taxInvoiceQueue: [],
    }
  }
})

const requested = computed(() => products.value.filter((product) => product.status === 'REQUESTED'))

const kpis = computed(() => [
  {
    label: '입금 미진행',
    value: ops.value.pendingPayments,
    unit: '건',
    icon: 'mdi-cash-clock',
    tone: 'amber',
    to: '/admin/orders?section=unnotified',
    alert: ops.value.pendingPayments > 0,
  },
  {
    label: '입금 알림',
    value: ops.value.payNotified,
    unit: '건',
    icon: 'mdi-bell-ring-outline',
    tone: 'amber',
    to: '/admin/orders?section=notified',
    alert: ops.value.payNotified > 0,
  },
  {
    label: '클레임 대기',
    value: ops.value.claimPending,
    unit: '건',
    icon: 'mdi-package-variant-remove',
    tone: 'red',
    to: '/admin/orders?work=claim',
    alert: ops.value.claimPending > 0,
  },
  {
    label: '미출고',
    value: ops.value.unshipped,
    unit: '건',
    icon: 'mdi-truck-delivery-outline',
    tone: 'green',
    to: '/admin/orders?section=shipping',
    alert: ops.value.unshipped > 0,
  },
  {
    label: '세금계산서',
    value: ops.value.taxInvoicePending,
    unit: '건',
    icon: 'mdi-file-document-outline',
    tone: 'violet',
    to: '/admin/orders?tax=1',
    alert: ops.value.taxInvoicePending > 0,
  },
  {
    label: '검수요청',
    value: counts.value.REQUESTED ?? 0,
    unit: '건',
    icon: 'mdi-clipboard-alert-outline',
    tone: 'violet',
    to: '/admin/products?status=REQUESTED',
    alert: (counts.value.REQUESTED ?? 0) > 0,
  },
])

const tasks = computed(() => [
  {
    label: '입금 미진행',
    count: ops.value.pendingPayments,
    to: '/admin/orders?section=unnotified',
  },
  {
    label: '입금 알림',
    count: ops.value.payNotified,
    to: '/admin/orders?section=notified',
  },
  {
    label: '상품 검수',
    count: counts.value.REQUESTED ?? 0,
    to: '/admin/products?status=REQUESTED',
  },
  {
    label: '클레임 처리',
    count: ops.value.claimPending,
    to: '/admin/orders?work=claim',
  },
  {
    label: '미출고 확인',
    count: ops.value.unshipped,
    to: '/admin/orders?section=shipping',
  },
  {
    label: '세금계산서',
    count: ops.value.taxInvoicePending,
    to: '/admin/orders?tax=1',
  },
])

const taskTotal = computed(() => tasks.value.reduce((sum, task) => sum + task.count, 0))

const steps = [
  { title: '1. 요청 내용 확인', desc: '판매업체가 보낸 상품명 · 가격 · 요청 메모를 먼저 봅니다.' },
  { title: '2. 내용 보강', desc: '이미지, 상세 페이지, 스펙, 옵션, 해시태그를 관리자가 채웁니다.' },
  { title: '3. 미리보기', desc: '구매 기관에게 보일 화면을 확인합니다.' },
  { title: '4. 승인 또는 반려', desc: '승인하면 바로 몰에 올라가고, 반려하면 사유가 판매업체에 전달됩니다.' },
]

function formatDate(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return `${date.getMonth() + 1}.${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`
}
</script>

<style lang="scss" src="./AdminDashboardPage.scss"></style>
